import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroller";

import { EmptyState, LoadingState } from "~/common/components";
import { PATIENT_STATUS } from "~/common/constants";
import { MergedMedicationRecord } from "~/common/types";
import {
  useGetPatient,
  useGetPatientFeeds,
  useIsTeamMemberStaff,
  useReviewInteractions,
} from "~/providers/hooks";
import { generatePatientCheckinOrder } from "~/providers/utils";
import { PatientFeed } from "~/providers/utils/types/interactions";
import {
  BundleFeed,
  CheckInOrderedFeed,
  CheckInSubmittedFeed,
  ConsentAcknowledgedFeed,
  RecurrentCheckInOrderedFeed,
  SelectedPatientCheckinType,
} from "~/providers/utils/types/patientFeeds";
import { PatientCheckinDrawer } from "../patientCheckins";
import { ActivityFeedCard } from "./activityFeedComponents/ActivityFeedCard";
import { ActivityFeedOrderDetailsDrawer } from "./activityFeedComponents/ActivityFeedOrderDetailsDrawer";
import { SignedConsentsDrawer } from "./activityFeedComponents/SignedConsentsDrawer";
import { MedicationDetailsDrawer } from "./medications";

interface ActivityFeedProps {
  patientId: string;
}

export interface PatientMedicationDetailsDrawerProps {
  medication: MergedMedicationRecord | undefined;
  medicationName: string;
}

export interface SelectedPatientCheckin {
  feed:
    | CheckInOrderedFeed
    | RecurrentCheckInOrderedFeed
    | CheckInSubmittedFeed
    | BundleFeed;
  type: SelectedPatientCheckinType;
  isProviderOrdered: boolean;
}

export const ActivityFeed = ({ patientId }: ActivityFeedProps) => {
  const { isStaff } = useIsTeamMemberStaff();
  const { data: patient } = useGetPatient(patientId);

  const [signedConsentsActivity, setSignedConsentsActivity] =
    useState<ConsentAcknowledgedFeed>();

  const [selectedPatientCheckin, setSelectedPatientCheckin] =
    useState<SelectedPatientCheckin>();

  const [selectedMedication, setSelectedMedication] = useState<
    PatientMedicationDetailsDrawerProps | undefined
  >();

  const { data, loadMore, hasNextPage, isFetched, isLoading } =
    useGetPatientFeeds(patientId);

  const { reviewInteractionsMutation } = useReviewInteractions(patientId);

  const isInConsultation =
    patient?.organization_patients[0].status === PATIENT_STATUS.consultation;

  useEffect(() => {
    if (isFetched) {
      !isStaff && !isInConsultation && reviewInteractionsMutation();
    }
  }, [reviewInteractionsMutation, isFetched, isStaff, isInConsultation]);

  const totalInteractions = data?.pages[0]?.filtered_count;

  if (isLoading) {
    return <LoadingState className="h-full" />;
  }

  if (!totalInteractions || totalInteractions === 0) {
    return <EmptyState message="There is no recent activity to show." />;
  }

  const currentOrder =
    selectedPatientCheckin &&
    generatePatientCheckinOrder(selectedPatientCheckin);

  return (
    <InfiniteScroll
      loadMore={loadMore}
      hasMore={hasNextPage}
      loader={<LoadingState key={patientId} />}
      useWindow={false}
    >
      <div className="flex w-full flex-col gap-3 px-14 py-5">
        {data?.pages.flatMap((pageData) =>
          pageData.data.map((feedOption: PatientFeed) => (
            <ActivityFeedCard
              key={feedOption.id}
              feedOption={feedOption}
              viewSignedConsents={(signedConsentsActivity) =>
                setSignedConsentsActivity(signedConsentsActivity)
              }
              viewPatientCheckInDetails={setSelectedPatientCheckin}
              viewMedicationDetails={(
                medicationData: PatientMedicationDetailsDrawerProps,
              ) => setSelectedMedication(medicationData)}
            />
          )),
        )}
      </div>
      <SignedConsentsDrawer
        isOpen={!!signedConsentsActivity}
        onClose={() => setSignedConsentsActivity(undefined)}
        signedConsentsActivity={signedConsentsActivity}
      />
      <ActivityFeedOrderDetailsDrawer
        activityFeedOrder={currentOrder}
        handleClose={() => {
          setSelectedPatientCheckin(undefined);
        }}
        allowDeleteOrder={false}
      />
      <PatientCheckinDrawer
        handleClose={() => setSelectedPatientCheckin(undefined)}
        patientCheckIn={selectedPatientCheckin}
      />
      <MedicationDetailsDrawer
        medication={selectedMedication?.medication}
        medicationName={selectedMedication?.medicationName ?? ""}
        handleClose={() => setSelectedMedication(undefined)}
      />
    </InfiniteScroll>
  );
};
